import { t } from "@lingui/macro"
import { Box, Button, Stack, useTheme } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useMemo } from "react"

import { PollModalStore } from "src/modals/polls/store"
import { PollOptions } from "src/modals/polls/components/PollOptions"
import { Add24 } from "src/components/icons/Add24"
import { Icon } from "src/components/icons"
import { useStore } from "src/store/lib/useStore"
import { ModalTextField } from "src/components/ModalTextField"
import {
    POLL_FIELD_TYPE_DROPDOWN_OPTIONS,
    POLL_CONSTANTS,
} from "src/modals/polls/constants/constants"
import { FieldTypeDropdown } from "src/components/Inputs/FieldTypeDropdown"
import {
    IQuestionsTabSectionsProps,
    PollTypeKeys,
} from "src/modals/polls/types"
import { ContentWrapper } from "src/modals/polls/styled"

export const QuestionsConfigSection = observer(
    ({ isViewMode }: IQuestionsTabSectionsProps) => {
        const store = useStore(PollModalStore)
        const theme = useTheme()

        const options = store.form.get("options")

        const handleQuestionChange = useCallback(
            (event: React.ChangeEvent<HTMLInputElement>) => {
                if (store.isReadOnly) return
                const value = event.target.value
                store.form.set("question", value)
                store.form.set("name", value)
            },
            [store.isReadOnly, store.form],
        )

        const handleOptionChange = useCallback(
            (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
                if (store.isReadOnly) return
                const updatedOptions = [...options]
                updatedOptions[index] = event.target.value
                store.form.set("options", updatedOptions)
                store.form.validateRequiredFields([
                    {
                        field: "options",
                        validate: store.validateOptions,
                    },
                ])
            },
            [store.isReadOnly, options, store.form, store.validateOptions],
        )

        const handleRemoveOption = useCallback(
            (index: number) => {
                if (store.isReadOnly) return
                const updatedOptions = [...options]
                updatedOptions.splice(index, 1)
                store.form.set("options", updatedOptions)
            },
            [store.isReadOnly, options, store.form],
        )

        const handleAddOption = useCallback(() => {
            if (store.isReadOnly) return
            store.form.set("options", [...options, ""])
        }, [store.isReadOnly, options, store.form])

        const handleTypeChange = useCallback(
            (value: string) => {
                store.form.set("questionType", value as PollTypeKeys)
                store.form.set("options", [...options])
            },
            [options, store.form],
        )

        const isDisabled = useMemo(
            () => store.isReadOnly || isViewMode,
            [store.isReadOnly, isViewMode],
        )

        const iconProps = useMemo(
            () => ({
                icon: <Add24 />,
                size: POLL_CONSTANTS.DEFAULT_ICON_SIZE,
                color: isDisabled
                    ? theme.palette.text.disabled
                    : theme.palette.info.main,
            }),
            [isDisabled, theme.palette.text.disabled, theme.palette.info.main],
        )

        const renderPollOption = useCallback(
            (option: string, index: number) => {
                const showRemoveButton =
                    options.length > POLL_CONSTANTS.MIN_OPTIONS &&
                    !store.isReadOnly

                return (
                    <PollOptions
                        key={index}
                        value={option}
                        onChange={(e) => handleOptionChange(index, e)}
                        onRemove={() => handleRemoveOption(index)}
                        showRemoveButton={showRemoveButton}
                        index={index}
                        disabled={isDisabled}
                    />
                )
            },
            [
                options.length,
                store.isReadOnly,
                isDisabled,
                handleOptionChange,
                handleRemoveOption,
            ],
        )

        const pollOptions = useMemo(
            () => options.map(renderPollOption),
            [options, renderPollOption],
        )

        const addOptionButton = useMemo(
            () =>
                !store.isReadOnly && (
                    <Button
                        startIcon={<Icon {...iconProps} />}
                        color="info"
                        onClick={handleAddOption}
                        sx={{ alignSelf: "flex-start" }}
                        disabled={isDisabled}
                    >
                        {t`poll.question-config.add-option-button`}
                    </Button>
                ),
            [store.isReadOnly, iconProps, handleAddOption, isDisabled],
        )

        return (
            <Stack sx={{ gap: 2, width: "100%" }}>
                <ContentWrapper>
                    <FieldTypeDropdown
                        item={{
                            value: store.form.get("questionType"),
                            errorMessage:
                                store.form.error("questionType") ?? "",
                        }}
                        onSelectValueChange={handleTypeChange}
                        disabled={isDisabled}
                        dropdownOptions={POLL_FIELD_TYPE_DROPDOWN_OPTIONS}
                    />
                    <Box sx={{ flex: 1, mt: 2 }}>
                        <ModalTextField
                            variant="default"
                            label={t`poll.question-config.question-label`}
                            value={store.form.get("question")}
                            onChange={handleQuestionChange}
                            error={Boolean(store.form.error("question"))}
                            helperText={store.form.error("question")}
                            disabled={isDisabled}
                            maxLength={POLL_CONSTANTS.MAX_QUESTION_LENGTH}
                        />
                    </Box>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        {pollOptions}
                        {addOptionButton}
                    </Stack>
                </ContentWrapper>
            </Stack>
        )
    },
)
